"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { getPosts, getFeaturedImageUrl } from "@/lib/cms";
import type { WordPressPost } from "@/lib/cms";
import { usePageMetadata } from "@/lib/usePageMetadata";

export default function StoriesPage() {
  const [posts, setPosts] = useState<WordPressPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch WordPress posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await getPosts({
          page,
          per_page: 12,
          orderby: "date",
          order: "desc",
          _embed: true,
        });
        setPosts(response.posts);
        setTotalPages(response.totalPages);
      } catch (error) {
        console.error("Error fetching stories from WordPress:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [page]);

  // Update page metadata
  usePageMetadata({
    title: "Stories from the North",
    description: "Read stories, articles, and insights from local vendors and adventurers in Alaska and the Pacific Northwest.",
    path: "stories",
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const stripHtml = (html: string) => {
    return html.replace(/<[^>]*>/g, "").trim();
  };

  return (
    <div className="bg-[#F8F5E9] min-h-screen pb-24">
      {/* Hero */}
      <motion.header
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        transition={{duration: 0.6}}
        className="text-white border-b"
        style={{
          background: "linear-gradient(to bottom, #0A3740, #0E4A52)",
          borderColor: "rgba(212, 175, 55, 0.4)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-14 space-y-3">
          <p
            className="text-xs uppercase tracking-[0.25em]"
            style={{color: "rgba(248, 245, 233, 0.7)"}}
          >
            Stories from the North
          </p>
          <h1
            className="text-4xl font-bold tracking-tight"
            style={{fontFamily: "var(--font-montserrat)", color: "#F8F5E9"}}
          >
            Stories & Articles
          </h1>
          <p
            className="text-sm md:text-base max-w-2xl"
            style={{color: "rgba(248, 245, 233, 0.85)"}}
          >
            Discover stories, insights, and adventures from local pilots, guides, and outfitters across Alaska and the Pacific Northwest.
          </p>
        </div>
      </motion.header>

      <main className="max-w-6xl mx-auto px-6 mt-10">
        {loading && (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-sm border p-6 animate-pulse"
                style={{borderColor: "rgba(10, 55, 64, 0.08)"}}
              >
                <div className="h-48 bg-gray-200 rounded-lg mb-4" />
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            ))}
          </div>
        )}

        {!loading && posts.length === 0 && (
          <motion.div
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 text-center"
            style={{borderColor: "rgba(10, 55, 64, 0.08)"}}
          >
            <p className="text-sm text-slate-600 mb-2">
              No stories available yet.
            </p>
            <p className="text-sm text-slate-500">
              Check back soon for stories from local vendors and adventurers.
            </p>
          </motion.div>
        )}

        {!loading && posts.length > 0 && (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post, index) => {
                const featuredImage = getFeaturedImageUrl(post);
                const excerpt = stripHtml(post.excerpt.rendered || "");
                
                return (
                  <motion.article
                    key={post.id}
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6, delay: index * 0.1}}
                  >
                    <Link
                      href={`/stories/${post.slug}`}
                      className="block bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
                      style={{borderColor: "rgba(10, 55, 64, 0.08)"}}
                    >
                      {featuredImage && (
                        <div className="relative w-full h-48 overflow-hidden">
                          <Image
                            src={featuredImage}
                            alt={post.title.rendered}
                            fill
                            className="object-cover"
                          />
                        </div>
                      )}
                      <div className="p-6 space-y-3">
                        <div className="space-y-2">
                          <h2
                            className="text-xl font-semibold line-clamp-2"
                            style={{
                              fontFamily: "var(--font-montserrat)",
                              color: "#0A3740",
                            }}
                            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                          />
                          <p className="text-xs text-slate-500">
                            {formatDate(post.date)}
                          </p>
                        </div>
                        {excerpt && (
                          <p className="text-sm text-slate-700 line-clamp-3">
                            {excerpt}
                          </p>
                        )}
                        <div className="pt-2">
                          <span
                            className="text-sm font-medium underline"
                            style={{color: "#0A3740"}}
                          >
                            Read more →
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{delay: 0.5}}
                className="mt-12 flex justify-center items-center gap-4"
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: page === 1 ? "rgba(10, 55, 64, 0.1)" : "#D4AF37",
                    color: page === 1 ? "rgba(10, 55, 64, 0.5)" : "#0A3740",
                  }}
                >
                  Previous
                </button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: page === totalPages ? "rgba(10, 55, 64, 0.1)" : "#D4AF37",
                    color: page === totalPages ? "rgba(10, 55, 64, 0.5)" : "#0A3740",
                  }}
                >
                  Next
                </button>
              </motion.div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

